import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { getPublicKey, getCheckoutSession } from '../services/api/stripe_cli';
import styled from 'styled-components';
import { COLORS } from '../values/colors';
import { url_api, api_version } from '../sources.js';
import Button from '../store/components/Button';
import Form from '../store/components/Form';
import mixpanel from 'mixpanel-browser';
import * as Sentry from "@sentry/react";
import { useSelector } from 'react-redux';
import { signupUser } from '../services/api/register.js';

const Store = (props) => {

    const [stripe, setStripe] = useState(null);
    const [showWaitlist, setShowWaitlist] = useState(false);
    const [emailError, setEmailError] = useState(null);
    const [country, setCountry] = useState(null);
    const [isInternational, setIsInternational] = useState(false);
    const [isCheckoutStarted, setIsCheckoutStarted] = useState(false);
    const [checkoutEmail, setCheckoutEmail] = useState('');
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [selectedCheckoutMode, setSelectedCheckoutMode] = useState(null);

    const user = useSelector(state => state.user.value);

    useEffect(() => {
        getPublicKey().then(data => {
            loadStripe(data.public_key).then(stripe => {
                setStripe(stripe);
            });
        });

        fetch("https://ipinfo.io/json?token=a83a6e1425dee9")
        .then((response) => response.json())
        .then((response) => {
            setCountry(response.country);
            setIsInternational(response.country != 'US' && response.country != 'CA');
        })
        .catch((error) => {
            setCountry('US');
            Sentry.captureException(error);
        });
        
    }, []);

    const handleJoinClick = (e) => {
        e.preventDefault();

        var code = null;
        if (props.checkoutCode)
            code = props.checkoutCode;
        else
            code = localStorage.getItem('checkoutCode');

        mixpanel.track('Join Clicked', {
            'Checkout Code': code,
            'International': isInternational,
            'Email': checkoutEmail,
            'New User': !user
        });
        
        const checkoutMode = e.currentTarget.getAttribute('checkoutmode');
        
        // If user is already signed in, go directly to Stripe checkout
        if (user) {
            handleStripeCheckoutRedirect(checkoutMode);
        } else {
            // Otherwise, show the email collection step
            setSelectedCheckoutMode(checkoutMode);
            setIsCheckoutStarted(true);
        }
    }

    const handleStripeCheckoutRedirect = (checkoutMode) => {
        getCheckoutSession(checkoutMode, isInternational, user ? user.email : checkoutEmail)
        .then((session) => {
            const code = props.checkoutCode || localStorage.getItem('checkoutCode');
            mixpanel.track('Redirected to Stripe', {
                'Checkout Code': code, 
                'International': isInternational,
                'Email': checkoutEmail,
                'New User': !user
            });
            
            stripe.redirectToCheckout({
                sessionId: session.id
            });
        });
    }

    const handleEmailSubmit = (e) => {
        e.preventDefault();
        
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(checkoutEmail.toLowerCase())) {
            setEmailError("Please enter a valid email address.");
            return;
        }

        signupUser(checkoutEmail)
        .then((response) => {
            setEmailError(null);
            mixpanel.identify(response.id);
            mixpanel.people.set({
                "$email": checkoutEmail
            });
            setShowConfirmation(true);
            setTimeout(() => {
                handleStripeCheckoutRedirect(selectedCheckoutMode);
            }, 1500);
        })
        .catch((error) => {
            Sentry.captureException(error);
            setEmailError("An error occurred. Please try again, or log in.");
        }); 
    }
    
    const handleEmailChange = (e) => {
        setCheckoutEmail(e.target.value);
        if (emailError) setEmailError(null);
    }
    
    const handleBackToOptions = () => {
        setIsCheckoutStarted(false);
        setShowConfirmation(false);
        setCheckoutEmail('');
        setEmailError(null);
    }

    const handleNotifyMe = (e) => {
        e.preventDefault();
        let email = e.target.parentElement.querySelector('input').value;
        email = email.trim();
        email = email.toLowerCase();

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.toLowerCase())) {
            setEmailError("Please enter a valid email address.");
            return;
        }

        e.target.parentElement.querySelector('input').disabled = true;
        e.target.parentElement.querySelector('Button').disabled = true;
        e.target.parentElement.querySelector('Button').innerText = "Sending...";

        fetch(url_api + api_version + '/freshworks/notify_me/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({email: email}),
        }).then(response => {
            if (response.status === 200) {
                setEmailError(null);
                e.target.parentElement.querySelector('input').value = '';
                e.target.parentElement.querySelector('input').placeholder = "Thank you!";
                e.target.parentElement.querySelector('Button').innerText = "Notify Me";
            } else {
                e.target.parentElement.querySelector('input').disabled = false;
                e.target.parentElement.querySelector('Button').disabled = false;
                e.target.parentElement.querySelector('Button').innerText = "Notify Me";
                setEmailError("An error occurred. Please try again.");
            }
        })
    }

    const handleToggleCheckout = (e) => {
        e.preventDefault();
        setIsInternational(!isInternational);
    }

    return (
        <StoreContainer>
            <OptionsContainer 
                align={props.align}
                className={isCheckoutStarted ? "slide-left" : ""}
            >
                {props.includeFree &&
                <OptionBox>
                    <h3>Basic</h3>
                    <FeatureList>
                        <li>No Camera</li>
                        <li>Orchid Cloud Access</li>
                            <SubList>
                                <li>Unlimited case viewing</li>
                                <li>No case uploading</li>
                            </SubList>
                    </FeatureList>
                    <CheckoutBox>
                        <p>Invite Only</p>
                    </CheckoutBox>
                </OptionBox>
                }
                {props.includePro &&
                <OptionBox>
                    <h3>Professional</h3>
                    <FeatureList>
                        <li>No Camera</li>
                        <li>Full Orchid Cloud Access</li>
                        <SubList>
                            <li>Unlimited case storage</li>
                            <li>Case editing</li>
                            <li>Case sharing</li>
                        </SubList>
                    </FeatureList>
                    <CheckoutBox>
                        <p>$75/month</p>
                        <Button 
                            width="100%"
                            text={user ? "Upgrade Now" : "Get Started"}
                            color='black'
                            textColor='white'
                            checkoutmode="platform_only" 
                            onClick={handleJoinClick}
                            disabled={country === null || user?.tier === 'pro'}
                        />
                    </CheckoutBox>
                </OptionBox>
                }
                {props.includeBundle &&
                <OptionBox>
                    <PromotionBox text="Early Adopter Price" />
                    <h3>Professional</h3>
                    <FeatureList>
                        <li>Orchid Camera</li>
                        <li>Full Orchid Cloud Access</li>
                        <SubList>
                            <li>Unlimited case storage</li>
                            <li>Case editing</li>
                            <li>Case sharing</li>
                        </SubList>
                    </FeatureList>
                    <CheckoutBox id="camera-bundle-price-box">
                        <p>
                            {!isInternational && country!=null &&
                            <Promotion>Free Shipping</Promotion>}
                            <b>$300</b> + $240/year
                        </p>
                        <Button 
                            width="100%"
                            text={user ? "Upgrade Now" : "Get Started"}
                            color='black'
                            textColor='white'
                            checkoutmode="platform_camera_bundle" 
                            onClick={handleJoinClick}
                            disabled={country === null || user?.tier === 'pro'}
                        />
                    </CheckoutBox>
                </OptionBox>
                }
            </OptionsContainer>
            
            {/* Only show the email collection form if checkout is started and user is not logged in */}
            {isCheckoutStarted && !user && (
                <CheckoutFormContainer className="slide-right">
                    <h2>Create Your Account</h2>
                    <StepSubtitle>Step 1 of 2</StepSubtitle>
                    {showConfirmation ? (
                        <ConfirmationMessage>
                            Check your email for a platform invite!
                            <br />
                            <span style={{fontSize: "0.8em"}}>Redirecting you to checkout...</span>
                        </ConfirmationMessage>
                    ) : (
                        <EmailFormContainer>
                            <EmailInput 
                                type="email" 
                                placeholder="Your Email Address"
                                value={checkoutEmail}
                                onChange={handleEmailChange}
                                required
                            />
                            {emailError && <EmailErrorMessage>{emailError}</EmailErrorMessage>}
                            
                            <LoginLink href={`/login?next=${encodeURIComponent(window.location.pathname)}`}>
                                Already have an account? Sign in
                            </LoginLink>
                            
                            <ButtonsContainer>
                                <Button 
                                    width="45%"
                                    text="Back"
                                    color='white'
                                    textColor='black'
                                    onClick={handleBackToOptions}
                                />
                                <Button 
                                    width="45%"
                                    text="Continue"
                                    color='black'
                                    textColor='white'
                                    onClick={handleEmailSubmit}
                                />
                            </ButtonsContainer>
                        </EmailFormContainer>
                    )}
                </CheckoutFormContainer>
            )}
            
            {/* Always render the container with same height, but conditionally show content */}
            <StoreFooterContainer empty={isCheckoutStarted}>
                {!isCheckoutStarted && (
                    <>
                        {!showWaitlist ?
                        <>
                            <div style={{marginTop: "0px"}}>
                                <a 
                                  href="#"
                                  onClick={handleToggleCheckout} 
                                  style={{fontSize:"0.75em", textDecoration: "none", cursor:"pointer"}}>
                                    {isInternational ? "Visit our North American store." : "Visit our international store."}
                                </a>
                            </div>
                        </>
                        :
                        <div
                            style={{position: "relative"}}
                        >
                            <ErrorMessage>
                                &#9888; &nbsp; Low inventory, be the first to know!
                            </ErrorMessage>
                            <Form 
                                placeholder="Your Email"
                                text="Notify Me!"
                                buttonColor="black"
                                buttonTextColor="white"
                                formName="LIST"
                                formValue="WaitList"
                                formCountry={country}
                                formType={user ? 'Upgrade' : 'New'}
                                width="520px"
                                />
                        </div>
                        }
                    </>
                )}
            </StoreFooterContainer>
        </StoreContainer>
    );
}

export default Store;

Store.defaultProps = {
    includeFree: true,
    includePro: true,
    includeBundle: true,
    checkoutText: "Checkout",
    align: "center",
}

const StoreContainer = styled.div`
    display: flex;
    width: 100%;
    height: 100%;
    position: relative;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    overflow-x: hidden;
`;

const OptionsContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: "${props => props.align}";
    justify-content: center;
    padding: 20px;
    width: 100%;
    height: 100%;
    gap: 40px 20px;
    transition: transform 0.5s ease-in-out;
    position: relative;

    &.slide-left {
        transform: translateX(-100%);
    }
    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const OptionBox = styled.div`
    display: flex;
    flex-direction: column;
    width: 250px;
    align-items: center;
    justify-content: center;
    background-color: white;
    padding: 20px;
    border-radius: 10px;
    max-width: 300px;
    min-height: 320px;
    border: 1px solid darkgray;
    position:relative;
    white-space: nowrap;
    color:rgb(80, 80, 80);
    font-size: 1.1em;

    h3 {
        font-family: 'Poppins';
        font-size: 1.5em;
        font-weight: bold;
    }
`;

const Promotion = styled.div`
    background-color: #FDAD00;
    color: #000;
    padding: 5px 10px;
    border-radius: 5px;
    margin-bottom: 10px;
    font-size: .8em;
    text-align: center;
`;

const PromotionBox = styled.div`
    position: absolute;
    border: 2px solid #0D6EFD;
    border-top: 26px solid #0D6EFD;
    padding: 0px;
    margin-top: -21px;
    width: calc(100% + 4px);
    height: calc(100% + 25px);
    font-size: 1.1em;
    border-radius: 10px;
    pointer-events: none;
    font-weight: bold;
    white-space: nowrap;

    &::before {
        content: "${props => props.text}";
        position: absolute;
        top: -24px;
        left: 50%;
        transform: translateX(-50%);
        padding: 0 5px;
        font-size: 0.8em;
        color: white;
        border: none;
    }
`;

const FeatureList = styled.ul`
    width: 100%;
    margin-top:10px;
    text-align: left;
    list-style-type: none;
    margin-left:-40px;

    li {
        padding: 3px;
    }
`;

const SubList = styled.ul`
    width: 100%;
    margin-top: 0px;
    text-align: left;
    list-style-type: none;
    margin-left: -20px;
    font-size: .9em;

    li {
        padding: 3px;
    }
`;

const CheckoutBox = styled.div`
    width: 100%;
    margin-top: auto;
    text-align: left;
    padding: 0px 13px;
    margin-bottom: -10px;
`;

const ErrorMessage = styled.p`
    position: absolute;
    color: darkred;
    text-align: center;
    width: 100%;
    top: -35px;
    font-size: 1.1em; 
`;

const CheckoutFormContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    max-width: 500px;
    min-height: 345px;
    padding: 20px;
    background-color: white;
    border-radius: 10px;
    animation: slideIn 0.5s ease-in-out;
    border: 1px solid darkgray;
    color: rgb(80, 80, 80);
    font-size: 1.1em;

    h2 {
        margin-bottom: 5px;
        font-family: 'Poppins';
        font-size: 1.5em;
        font-weight: bold;
        text-align: center;
    }

    @keyframes slideIn {
        from {
            transform: translate(100%, -50%);
            opacity: 0;
        }
        to {
            transform: translate(-50%, -50%);
            opacity: 1;
        }
    }
    
    @media (max-width: 768px) {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
        max-width: 100%;
        transform: none;
        border-radius: 0;
        border: none;
        z-index: 1000;
        padding: 20px 20px 80px 20px; /* Increase bottom padding */
        justify-content: flex-start; /* Align content to the top on mobile */
        display: flex;
        overflow-y: auto; /* Allow scrolling if needed */
        animation: mobileSlideIn 0.5s ease-in-out;
        
        @keyframes mobileSlideIn {
            from {
                transform: translateX(100%);
                opacity: 0;
            }
            to {
                transform: translateX(0);
                opacity: 1;
            }
        }
    }
`;

const EmailFormContainer = styled.form`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 25px; /* Increased gap for more spacing between elements */
    margin-top: 30px; /* More space after the subtitle */
    flex-grow: 1;
    justify-content: space-between;
    position: relative; /* Added to make absolute positioning work */
    
    @media (max-width: 768px) {
        gap: 15px; /* Reduced gap on mobile */
        margin-top: 15px; /* Less space after subtitle on mobile */
        justify-content: flex-start; /* Change to flex-start on mobile */
        height: auto; /* Let height be determined by content on mobile */
        /* Fix for button interaction issues */
        overflow-y: auto;
        padding-bottom: 50px; /* Add padding at the bottom to ensure visibility */
    }
`;

const EmailInput = styled.input`
    width: 100%;
    padding: 15px; /* Slightly larger padding */
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 16px;
    outline: none;
    margin-top: 15px; /* Add some space at the top */

    &:focus {
        border-color: ${COLORS.orchid_blue};
        box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
    }
`;

const EmailErrorMessage = styled.p`
    color: #d32f2f;
    font-size: 14px;
    position: absolute; /* Position absolutely so it doesn't affect layout */
    top: 70px; /* Position below the input */
    left: 0;
    right: 0;
    margin: 0 auto;
    text-align: center;
    width: 100%;
`;

const ButtonsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-top: auto;
    padding-top: 30px; /* Add padding to push buttons lower */
    
    @media (max-width: 768px) {
        padding-top: 10px; /* Reduced padding on mobile */
        margin-top: 200px; /* Increased margin to move buttons further up */
        position: relative; /* Make position relative on mobile */
        z-index: 10; /* Ensure buttons are clickable */
        
        /* Apply styles to child buttons */
        button {
            position: relative;
            z-index: 20;
            transform: translateZ(0); /* Force hardware acceleration */
        }
    }
`;

const ConfirmationMessage = styled.div`
    padding: 30px;
    background-color: #e8f5e9;
    color: #2e7d32;
    border-radius: 5px;
    text-align: center;
    font-size: 18px;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin: auto;
    height: 60%;
    animation: fadeIn 0.3s ease-in-out;

    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

const StepSubtitle = styled.p`
    color: #757575;
    font-size: 14px;
    margin: 0 0 20px 0;
    text-align: center;
`;

const StoreFooterContainer = styled.div`
    width: 100%;
    max-width: 500px;
    text-align: center;
    margin-top: 10px;
    min-height: 30px; /* Increased minimum height */
    height: 30px; /* Fixed height to prevent any size changes */
    display: flex;
    justify-content: center;
    align-items: center;
    visibility: ${props => props.empty ? 'hidden' : 'visible'}; /* Keep layout but hide content when needed */
`;

const LoginLink = styled.a`
    color: ${COLORS.orchid_blue};
    font-size: 14px;
    text-align: center;
    margin-bottom: auto;
    cursor: pointer;
    text-decoration: none;
    
    &:hover {
        text-decoration: underline;
    }
`;